import { withApollo } from '@apollo/client/react/hoc';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import withAnalytics, { CIAnalytics } from 'common/analytics/analytics.provider';
import PendoAnalytics from 'common/analytics/pendo';
import { AuthActions } from 'common/auth/actions';
import { BaseRoles, RoleVariantEnum, UserSubType, UserType } from 'common/graphql/types';
import { useBrowserTabSync } from 'common/hooks/useBrowserTabSync';
import { AuditLoggerProvider } from 'common/log/ci.auditlogger';
import CIAppLogger from 'common/log/ci.logger';
import ConsoleLogger from 'common/log/console.logger';
import LoggerProvider, { CILogger } from 'common/log/log.provider';
import 'common/sentry/sentry';
import Telehealth from 'common/telehealth';
import { ComponentWithoutHOCProps } from 'common/types/component';
import { SuspenseFallback } from 'common/utils/loading';
import CINetworkBanner from 'common/viewcomp/general/offline/network-banner/ci_network_banner';
import { flow } from 'lodash';
import { ReactElement, Suspense } from 'react';
import { useDispatch, useStore } from 'react-redux';
import AppRoutes from 'routes';
import './app.scss';
import { useDetectPendoFailure } from 'common/hooks/useDetectPendoFailure';
import { AuthState } from 'common/auth/types';
import { useAutoRoleSync } from 'common/hooks/useAutoRoleSync';

declare global {
    interface Window {
        pendo?: {
            visitorId: string;
            identify: (
                visitor: {
                    id: string;
                    email: string;
                    userCreation: string;
                    username: string;
                    userType: UserType;
                    subType: UserSubType;
                    language: string;
                    preferredLanguage: string;
                    studyId: string;
                    siteId: string;
                    baseRole: BaseRoles;
                    roleVariant: RoleVariantEnum;
                    device: string;
                    operatingSystem: string;
                    osVersion: string;
                },
                account: { id: string; name?: string; createDate?: string }
            ) => void;
            clearSession: () => void;
        };
    }
}

const analytics = CIAnalytics.getInstance();
analytics.registerProvider(new PendoAnalytics());
analytics.initialize();

const logging = CILogger.getInstance();
if (process.env.NODE_ENV !== 'production') {
    logging.registerProvider(new ConsoleLogger());
}
logging.registerProvider(new CIAppLogger());
logging.initialize();

function App(): ReactElement {
    const store = useStore<{ auth: AuthState }>();
    const { auth } = store.getState();
    const dispatch = useDispatch();

    // Update Pendo if undefined
    if (auth.activeUser?.pk && window.pendo?.visitorId !== auth?.activeUser?.pk) {
        CIAnalytics.getInstance().identifyUser(auth.activeUser);
    }

    useBrowserTabSync();
    useDetectPendoFailure();
    useAutoRoleSync();

    return (
        <div
            id="app_container"
            className={classNames({
                'televisit_thumbnail': auth.thumbnailMode,
            })}
        >
            {auth.activeMeeting && (
                <Telehealth
                    {...(auth.meetingDetails || {})}
                    closeParent={(): void => {
                        dispatch(AuthActions.updateTelevisitStatus({ id: '', name: '', isAdmin: '' }, false));
                    }}
                />
            )}
            <Suspense fallback={<SuspenseFallback />}>
                {Capacitor.isNativePlatform() && <CINetworkBanner />}
                <AppRoutes />
            </Suspense>
        </div>
    );
}

const AppWrapper: ComponentWithoutHOCProps<{}> = flow([withApollo, withAnalytics, LoggerProvider, AuditLoggerProvider])(
    App
);

export default AppWrapper;
